import 'cropperjs/dist/cropper.css';
import { AnimatePresence, motion } from 'framer-motion';
import { ChangeEvent, DragEvent, KeyboardEvent, useEffect, useRef, useState } from 'react';
import Cropper, { ReactCropperElement } from 'react-cropper';
import { createPortal } from 'react-dom';
import { useAuthenticatedImage } from '../Hooks/useAuthenticatedImage';
import { useAlert } from './AlertContext';
import Button from './Buttons';
import './UserPhotoInput.css';

const MAX_PHOTO_SIZE_BYTES = 2 * 1024 * 1024;
const ALLOWED_PHOTO_TYPES = ['image/jpeg', 'image/png'];
const OUTPUT_SIZE = 512;

type UserPhotoInputProps = {
  value: string;
  onChange: (value: string) => void;
  disabled?: boolean;
  organizationId?: string;
};

type CropSource = {
  src: string;
  type: string;
};

function UserPhotoInput({
  value,
  onChange,
  disabled = false,
  organizationId,
}: UserPhotoInputProps) {
  const { showAlert } = useAlert();

  const isDataUrl = value.startsWith('data:image');
  const savedImage = useAuthenticatedImage(isDataUrl ? null : value, organizationId);
  const previewSrc = isDataUrl ? value : savedImage.url;
  const previewLoading = !isDataUrl && savedImage.loading;

  const fileInputRef = useRef<HTMLInputElement>(null);
  const cropperRef = useRef<ReactCropperElement>(null);

  const [cropSource, setCropSource] = useState<CropSource | null>(null);
  const [isDragOver, setIsDragOver] = useState<boolean>(false);

  useEffect(() => {
    if (!cropSource) return undefined;

    const handleKeyDown = (event: globalThis.KeyboardEvent) => {
      if (event.key === 'Escape') setCropSource(null);
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [cropSource]);

  const openFilePicker = () => {
    if (!disabled) fileInputRef.current?.click();
  };

  const handleFile = (file?: File) => {
    if (!file) return;

    if (!ALLOWED_PHOTO_TYPES.includes(file.type)) {
      showAlert('warning', 'Only JPG or PNG files are allowed.');
      return;
    }
    if (file.size > MAX_PHOTO_SIZE_BYTES) {
      showAlert('warning', 'File is larger than 2 MB.');
      return;
    }

    const reader = new FileReader();
    reader.onload = () => setCropSource({ src: reader.result as string, type: file.type });
    reader.onerror = () => showAlert('warning', 'Failed to read the file.');
    reader.readAsDataURL(file);
  };

  const handleFileChange = (event: ChangeEvent<HTMLInputElement>) => {
    handleFile(event.target.files?.[0]);
    event.target.value = '';
  };

  const handleAvatarKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      openFilePicker();
    }
  };

  const handleDragOver = (event: DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    if (!disabled) setIsDragOver(true);
  };

  const handleDrop = (event: DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    setIsDragOver(false);
    if (!disabled) handleFile(event.dataTransfer.files[0]);
  };

  const applyCrop = () => {
    const cropper = cropperRef.current?.cropper;
    if (!cropper || !cropSource) return;

    const side = Math.max(1, Math.min(OUTPUT_SIZE, Math.round(cropper.getData().width)));
    const canvas = cropper.getCroppedCanvas({
      width: side,
      height: side,
      imageSmoothingEnabled: true,
      imageSmoothingQuality: 'high',
    });
    const mimeType = cropSource.type === 'image/png' ? 'image/png' : 'image/jpeg';

    onChange(canvas.toDataURL(mimeType, 0.92));
    setCropSource(null);
  };

  const avatarClassName = [
    'user-photo-avatar',
    !previewSrc && 'user-photo-avatar--empty',
    previewLoading && 'user-photo-avatar--loading',
    isDragOver && 'user-photo-avatar--drag',
    disabled && 'user-photo-avatar--disabled',
  ]
    .filter(Boolean)
    .join(' ');

  return (
    <div className="user-photo-container">
      <div className="user-photo-card">
        <div className="user-photo-avatar-wrap">
          <div
            className={avatarClassName}
            role={disabled ? undefined : 'button'}
            tabIndex={disabled ? undefined : 0}
            aria-label={disabled ? undefined : 'Upload photo'}
            onClick={openFilePicker}
            onKeyDown={handleAvatarKeyDown}
            onDragOver={handleDragOver}
            onDragLeave={() => setIsDragOver(false)}
            onDrop={handleDrop}
          >
            <div className="user-photo-frame">
              {previewSrc ? (
                <img className="user-photo-image" src={previewSrc} alt="User avatar" />
              ) : (
                !previewLoading && <i className="fa-solid fa-user user-photo-placeholder"></i>
              )}
            </div>
            {!disabled && !value && (
              <span className="user-photo-badge">
                <i className="fa-solid fa-camera"></i>
              </span>
            )}
          </div>
          {!disabled && value && (
            <button
              type="button"
              className="user-photo-remove"
              aria-label="Remove photo"
              onClick={() => onChange('')}
            >
              <i className="fa-solid fa-xmark"></i>
            </button>
          )}
        </div>

        <div className="user-photo-text">
          <div className="user-photo-title">Profile photo</div>
          <div className="user-photo-description">Shown in internal chats and the web widget.</div>
          <div className="user-photo-hint">JPG or PNG, up to 2 MB</div>
        </div>

        {!disabled && (
          <button type="button" className="user-photo-upload" onClick={openFilePicker}>
            <i className="fa-solid fa-arrow-up-from-bracket"></i>
            {value ? 'Change photo' : 'Upload photo'}
          </button>
        )}
      </div>

      <input
        ref={fileInputRef}
        type="file"
        accept={ALLOWED_PHOTO_TYPES.join(',')}
        onChange={handleFileChange}
        hidden
      />

      {createPortal(
        <AnimatePresence>
          {cropSource && (
            <motion.div
              className="confirmation-popup-overlay user-photo-crop-overlay"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.2 }}
            >
              <motion.div
                className="confirmation-popup user-photo-crop-popup"
                initial={{ scale: 0.9 }}
                animate={{ scale: 1 }}
                exit={{ scale: 0.9 }}
                transition={{ duration: 0.2 }}
              >
                <Button variant="close" size="round" onClick={() => setCropSource(null)} />
                <strong>Crop photo</strong>
                <span className="user-photo-crop-hint">Drag to reposition, scroll to zoom.</span>

                <Cropper
                  ref={cropperRef}
                  className="user-photo-cropper"
                  src={cropSource.src}
                  aspectRatio={1}
                  viewMode={1}
                  dragMode="move"
                  autoCropArea={1}
                  guides={false}
                  center={false}
                  background={false}
                  checkOrientation={false}
                  toggleDragModeOnDblclick={false}
                />

                <div className="user-photo-crop-buttons">
                  <Button background="color2" size="medium" onClick={applyCrop}>
                    Apply
                  </Button>
                  <Button variant="danger" size="medium" onClick={() => setCropSource(null)}>
                    Cancel
                  </Button>
                </div>
              </motion.div>
            </motion.div>
          )}
        </AnimatePresence>,
        document.body,
      )}
    </div>
  );
}

export default UserPhotoInput;
